import React, { FC, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import SearchFiltersModal from './SearchFiltersModal';
import { SearchFilterBlock } from '@web-kc/shared/components';
import { useTranslation } from 'next-i18next';
import { usePlanList } from '../providers';
import {
  getEnumOrganizations,
  useAPIQuery,
  usePlanForms,
  usePlanStatuses,
  usePlanTypes,
} from '@web-kc/core/api';
import { useDebouncedCallback } from 'use-debounce';

const PlanListFilter: FC = () => {
  const { t } = useTranslation(['plans']);
  const { searchFilters, setSearchFilters, companies, companyById } =
    usePlanList();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  // Загружаем справочники для отображения человеко-читаемых имен
  const { data: dataForms, isLoading: isLoadingForms } = usePlanForms();
  const { data: dataStatuses, isLoading: isLoadingStatuses } =
    usePlanStatuses();
  const { data: dataTypes, isLoading: isLoadingTypes } = usePlanTypes();
  const { data: organizations, isLoading: isLoadingOrganizations } =
    useAPIQuery(getEnumOrganizations);

  // Дебаунс для автопоиска по keyword
  const debouncedSearch = useDebouncedCallback((keyword: string) => {
    setSearchFilters({
      ...searchFilters,
      page: 0,
      id: keyword as never,
    });
  }, 1000);

  // Синхронизация keyword с searchFilters.id
  useEffect(() => {
    setKeyword(searchFilters.id?.toString() ?? '');
  }, [searchFilters.id]);

  // Автопоиск при вводе keyword
  useEffect(() => {
    if (
      keyword !== undefined &&
      keyword !== (searchFilters.id?.toString() ?? '')
    ) {
      debouncedSearch(keyword);
    }
  }, [keyword, debouncedSearch, searchFilters.id]);

  // Cleanup дебаунса
  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  // Обработчик submit кнопки поиска (отменяет дебаунс и выполняет сразу)
  const handleClickSearchButton = () => {
    debouncedSearch.cancel();
    setSearchFilters({
      ...searchFilters,
      page: 0,
      id: keyword as never,
    }).then();
  };

  // Функция для получения человеко-читаемого имени фильтра
  const getFilterDisplayName = (key: string, value: string | number) => {
    switch (key) {
      case 'id':
        return `${t('plans:filter.byId')}: ${value}`;
      case 'organization':
        return organizations?.find((item) => item.id === value)?.name || value;
      case 'company':
        let companyName = '';
        if (companyById && companyById.id === Number(value)) {
          companyName = companyById.fullName || companyById.name || '';
        } else {
          // Если не найдено в companyById, ищем в companies
          const company = companies?.content?.find(
            (c) => c.id === Number(value),
          );
          companyName = company?.fullName || company?.name || '';
        }
        // Если ничего не найдено, показываем ID
        if (!companyName) {
          companyName = `#${value}`;
        }
        return `${t('plans:company')}: ${companyName}`;
      case 'yearAfter':
        return `${t('plans:filter.yearAfter')}: ${value}`;
      case 'yearBefore':
        return `${t('plans:filter.yearBefore')}: ${value}`;
      case 'term':
        return dataTypes?.find((item) => item.id === value)?.name || value;
      case 'form':
        return dataForms?.find((item) => item.id === value)?.name || value;
      case 'sumFrom':
        return `${t('plans:filter.sumFrom')}: ${value}`;
      case 'sumTo':
        return `${t('plans:filter.sumTo')}: ${value}`;
      case 'status':
        return (
          dataStatuses?.find((item) => String(item.id) === String(value))
            ?.name || value
        );
      default:
        return value;
    }
  };

  // Проверка, показывать ли чип (данные должны быть загружены)
  const shouldShowChip = (key: string) => {
    switch (key) {
      case 'status':
        return !isLoadingStatuses && dataStatuses && dataStatuses.length > 0;
      case 'term':
        return !isLoadingTypes && dataTypes && dataTypes.length > 0;
      case 'form':
        return !isLoadingForms && dataForms && dataForms.length > 0;
      case 'organization':
        return (
          !isLoadingOrganizations && organizations && organizations.length > 0
        );
      case 'company':
        return true; // Показываем чип если есть company в фильтрах
      case 'id':
      case 'yearAfter':
      case 'yearBefore':
      case 'sumFrom':
      case 'sumTo':
        return true;
      default:
        return true;
    }
  };

  // Автоматическая генерация активных фильтров из searchFilters
  const activeFilters = Object.entries(searchFilters ?? {})
    .filter(
      ([key, value]) =>
        !['page', 'size', 'sort', 'modal'].includes(key) &&
        value !== undefined &&
        value !== '' &&
        value !== null &&
        shouldShowChip(key),
    )
    .map(([key, value]) => ({
      key,
      value: value as string | number,
      displayName: getFilterDisplayName(key, value as string | number),
    }));

  // Формируем appliedFilters для отображения в чипах (для обратной совместимости)
  const appliedFilters = activeFilters.reduce(
    (acc, filter) => {
      acc[filter.key] = filter.displayName;
      return acc;
    },
    {} as Record<string, string | number>,
  );

  // Удаление фильтра
  const handleRemoveFilter = (filterKey: string) => {
    const newFilters = {
      ...searchFilters,
      [filterKey]: undefined,
    };
    setSearchFilters(newFilters as never).then();

    // Если удаляем id, очищаем и keyword
    if (filterKey === 'id') {
      setKeyword('');
    }
  };

  const count = activeFilters.length;

  return (
    <Box>
      <SearchFilterBlock
        textValue={keyword}
        placeholder={t('plans:filter.byId')}
        textOnChange={setKeyword}
        onOpenModal={() => setOpen(true)}
        count={count}
        onSearch={handleClickSearchButton}
        appliedFilters={appliedFilters}
        onRemoveFilter={handleRemoveFilter}
        withTranslation={false}
      />
      <SearchFiltersModal open={open} handleClose={() => setOpen(false)} />
    </Box>
  );
};

export default PlanListFilter;
